import {
    IBookableEntity,
    IBooking,
    IBookingRecurrence,
    ISelectOption,
    IServiceTypesList,
    ITimeZone,
    TBookingRecurrence,
} from '../types';
import {
    ONSITE_CAPTIONING_GUARANTEED_PERIOD_MINUTES,
    ONSITE_CAPTIONING_MINIMUM_DURATION_MINUTES,
    ONSITE_CAPTIONING_MINIMUM_PERIOD_TO_MODIFY_HOURS,
    TOPIC_MODEL_CURATION_GUARANTEED_PERIOD_MINUTES,
} from '../constants/booking';
import {DateTime, DateTimeOptions, Duration, Info} from 'luxon';
import {IShortUser, IUser} from './settings/settings.service';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

export const emailIsValid = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export const between = (x: number, min: number, max: number): boolean => {
    return x >= min && x <= max;
}

export const getAppToken = () => {
    return JSON.parse(localStorage.getItem('app_token') || '')
}


export const detectImgsFromHtmlString = (str: string) => {
    let m;
    const imgs: string[] = []
    const rex =  /<img[^>]+src="?([^"\s]+)"?\s*/gi;

    while (m = rex.exec( str )) {
        imgs.push( m[1] );
    }

    return imgs
}

export const detectImgsFromHtmlStringAndFormat = (str: string) => {
    const base_url = getBackendBaseEndpoint('/v1/files/')
    const imgs = detectImgsFromHtmlString(str)
        .map((i: string) => {
            if (i.includes(base_url)) {
                const [org_id, type, filename] = i.replace(base_url, '').split('/')
                return {
                    org_id,
                    type,
                    filename
                }
            }
            return false
        }).filter(Boolean)

    return imgs
}

export const createImgUrl = (result: any) => {
    const { org_id, type, filename } = result
    const base_url = getBackendBaseEndpoint('/v1/files')
    return `${base_url}/${org_id}/${type}/${filename}`
}

export const getBackendBaseEndpoint = (suffix: string = '') => {
    return ((environment.apiServer.slice(-1) === '/')
    ? environment.apiServer.slice(0, -1)
    : environment.apiServer) + suffix;
}

export const getFrontendBaseEndpoint = (suffix: string = '') => {
    return environment.serverURL + suffix
}

export const generateString = (length: number) =>{
    const characters ='ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = ' ';
    const charactersLength = characters.length;
    // Rejection sampling avoids modulo bias from mapping a byte (0-255) onto a 62-character alphabet.
    const maxByte = 256 - (256 % charactersLength);
    const randomByte = new Uint8Array(1);
    for ( let i = 0; i < length; i++ ) {
        let byte: number;
        do {
            crypto.getRandomValues(randomByte);
            byte = randomByte[0];
        } while (byte >= maxByte);
        result += characters.charAt(byte % charactersLength);
    }

    return result;
}

/** Normalizes schedule time values from form controls (24h "H:mm" string or legacy { name } shapes). */
export const normalizeScheduleTimeString = function (time: unknown): string {
    if (time == null || time === '') {
        return '';
    }
    if (typeof time === 'string') {
        return time;
    }
    if (typeof time === 'object' && time !== null && 'name' in time) {
        const name = (time as { name?: string }).name;
        return name != null ? String(name) : '';
    }
    return String(time);
};

export const convertScheduleToDates = function (
    recurrence: TBookingRecurrence,
    start_date: string,
    start_time: string | unknown,
    end_time: string | unknown,
    end_date?: string,
    time_zone?: string
): [DateTime, DateTime] {
    const dateOptions: DateTimeOptions | undefined = (time_zone) ? {zone: time_zone} : undefined;
    const startTimeStr = normalizeScheduleTimeString(start_time);
    const endTimeStr = normalizeScheduleTimeString(end_time);

    let start = DateTime.fromISO(start_date, dateOptions);
    let end = DateTime.fromISO(start_date, dateOptions);
    if ((recurrence !== 'one-day booking') && end_date) {
        end = DateTime.fromISO(end_date, dateOptions);
    }

    if (startTimeStr) {
        const [startHours, startMinutes] = startTimeStr.split(':').map((_) => parseInt(_, 10));
        start = start.set({
            hour: Number.isNaN(startHours) ? 0 : startHours,
            minute: Number.isNaN(startMinutes) ? 0 : startMinutes,
        });
    }

    if (endTimeStr) {
        const [endHours, endMinutes] = endTimeStr.split(':').map((_) => parseInt(_, 10));
        end = end.set({
            hour: Number.isNaN(endHours) ? 0 : endHours,
            minute: Number.isNaN(endMinutes) ? 0 : endMinutes,
        });

        if (
            startTimeStr
            && start.endOf('day').diff(start, 'hours').hours < 2 // > 10PM
            && end.diff(end.startOf('day'), 'hours').hours < 5 // < 5AM
        ) {
            end = end.plus({day: 1});
        }

    }
    return [start, end];
};

export const getRecurrenceType = function (r: IBookingRecurrence): TBookingRecurrence {
    let resultingType: TBookingRecurrence = 'one-day booking';

    if (r && r.pattern) {
        switch (r.pattern) {
            case 'every_X_days':
                resultingType = 'daily';
                break;
            case 'every_X_weeks_on_bitdays':
                resultingType = 'weekly';
                break;
            case 'on_day_X_every_Y_months':
                resultingType = 'monthly';
                break;
            case 'on_X_bitday_every_Y_months':
                resultingType = 'monthly';
                break;
        }
    }
    return resultingType;
};

/**
 * Normalizes an epoch timestamp to ensure it's in seconds (Unix epoch) for backend communication.
 *
 * This function:
 * - Validates the input is a valid number (handles string conversion)
 * - Detects if the value is in milliseconds (too large) and converts to seconds
 * - Returns a valid Unix epoch in seconds
 *
 * Detection logic: If the value is > 4102444800 (year 2100 in seconds), it's likely in milliseconds
 *
 * @param epoch - The epoch timestamp (may be in seconds, milliseconds, or a string representation)
 * @returns The epoch timestamp in seconds (Unix epoch) for backend communication
 * @throws Error if the epoch value is invalid
 */
export const normalizeEpochToSeconds = function (epoch: number | string | null | undefined): number {
    // Convert string to number if needed
    let numEpoch: number;
    if (typeof epoch === 'string') {
        numEpoch = Number(epoch);
    } else {
        numEpoch = epoch as number;
    }

    if (numEpoch === null || numEpoch === undefined || isNaN(numEpoch)) {
        throw new Error('Invalid epoch value: must be a valid number');
    }

    // Year 2100 in seconds: 4102444800
    // If the value is larger than this, it's likely in milliseconds
    const YEAR_2100_IN_SECONDS = 4102444800;

    if (numEpoch > YEAR_2100_IN_SECONDS) {
        // Value is in milliseconds, convert to seconds
        return Math.floor(numEpoch / 1000);
    }

    // Value is already in seconds
    return Math.floor(numEpoch);
};

/**
 * Gets the display epoch timestamp in milliseconds for a booking.
 *
 * EPOCH HANDLING RULES (Consistent across the codebase):
 *
 * 1. Backend Communication:
 *    - Backend sends/receives start_epoch in SECONDS (Unix epoch)
 *    - When creating bookings: send start_epoch in SECONDS (step.service.ts, createInstantLexiBooking$)
 *    - When updating bookings: send start_epoch in SECONDS (update-booking-dialog.component.ts)
 *    - ALWAYS use normalizeEpochToSeconds() before sending to backend to ensure valid Unix epoch
 *
 * 2. Frontend Data Storage:
 *    - Booking.start_epoch: Converted to MILLISECONDS in bookings.service.ts (lines 259, 274, 341, 383)
 *      This happens when fetching bookings from the backend
 *    - Session.start_epoch: Always remains in SECONDS (never converted)
 *
 * 3. Display:
 *    - customDate pipe expects MILLISECONDS
 *    - This function handles the conversion: sessions (seconds) -> milliseconds, bookings (already milliseconds)
 *
 * @param booking - The booking object
 * @returns The epoch timestamp in milliseconds for display
 */
export const getDisplayEpoch = function (booking: IBooking): number {
    // booking.start_epoch is already in milliseconds (converted in bookings.service.ts)
    // For completed bookings, the controller sets it to the individual session's start time
    if (booking.start_epoch) {
        return booking.start_epoch;
    }
    // Fallback for cases where booking.start_epoch is absent
    const [firstSession] = booking.sessions || [];
    if (firstSession?.start_epoch) {
        return firstSession.start_epoch * 1000;
    }
    return booking.start_epoch;
};

export const getUserTitle = function (user: IUser | IShortUser): string {
    const userTitle = [user.first_name, user.last_name].map(_ => (_) ? _.trim() : '');
    return (userTitle.filter(Boolean).length) ? userTitle.join(' ') : (((user as IUser).email) ?? ('User id - ' + user.user_id));
};


const USTimeZoneCache: Set<string> = new Set();
// Test-only hook: this cache is a singleton for the lifetime of the module (i.e. the whole Karma
// run), so without a way to clear it, whether utils.spec.ts's own test can deterministically
// exercise both branches of the mapping path below depends on which spec across the ~350-file
// suite happens to call isInUSTimeZone$() first (Jasmine randomises spec order) — see the
// `isInUSTimeZone$` describe block in utils.spec.ts.
export const __resetUSTimeZoneCacheForTests = (): void => USTimeZoneCache.clear();
export const isInUSTimeZone$ = function (timeZones$: Observable<ITimeZone[]>, currentTimeZone: string): Observable<boolean> {
    const currentZone = Info.normalizeZone(currentTimeZone);

    // if cache is not empty - filter function was called minimum once
    if (!!USTimeZoneCache.size) {
        return of(USTimeZoneCache.has(currentZone.name));
    }
    return timeZones$.pipe(
        map(timeZones => {
            let result = false;
            const tz = timeZones.filter(_ => {
                if (_.country?.includes('US')) {
                    USTimeZoneCache.add(_.time_zone);
                    return (currentZone.name === _.time_zone);
                }
                return false;
            });
            if (tz.length) {
                result = true;
            }

            return result;
        })
    );
};

export const minutesToHumanRepresentation = function (minutes: number): string {
    let result: string[] = [];

    const d = Duration.fromObject({minutes}).normalize().shiftTo('days', 'hours', 'minutes').toObject();

    result = Object.entries(d).map(([unit, value]) => {
        if (value > 0) {
            return [value, unit].join(' ');
        }
        return '';
    }).filter(Boolean);


    return result.join(' ');
};

export function isOnsiteCaptioningByServiceTypeName(
    serviceTypeName: string | undefined,
    serviceTypes: Pick<IServiceTypesList, 'name' | 'srv_id'>[]
): boolean {
    if (!serviceTypeName) {
        return false;
    }
    const found = serviceTypes.find((st) => st.name === serviceTypeName);
    return found?.srv_id === 'oc';
}

export function effectiveShortNoticeGuaranteedPeriodMinutes(
    orgGuaranteedMinutes: number,
    isOnsite: boolean,
    isTopicModelCuration: boolean = false
): number {
    return isOnsite
        ? ONSITE_CAPTIONING_GUARANTEED_PERIOD_MINUTES
        : isTopicModelCuration
            ? TOPIC_MODEL_CURATION_GUARANTEED_PERIOD_MINUTES
            : orgGuaranteedMinutes;
}

export function effectiveCancellationNoticeHours(
    serviceTypeName: string | undefined,
    serviceTypes: Pick<IServiceTypesList, 'name' | 'srv_id'>[]
): number {
    return isOnsiteCaptioningByServiceTypeName(serviceTypeName, serviceTypes)
        ? ONSITE_CAPTIONING_MINIMUM_PERIOD_TO_MODIFY_HOURS
        : environment.booking.minimumPeriodToModify;
}

export function buildMinimumHoursBeforeBookingModifyMessage(hours: number): string {
    return `You can no longer change the date and time of this booking as there is less than ${hours} hours before it is scheduled to commence. Please cancel the booking and make a new one if required.`;
}

export function buildShortNoticeConfirmationMessage(
    guaranteedPeriodMinutes: number,
    isInterpretingServiceType: boolean,
    isTopicModelCurationServiceType: boolean = false
): string {
    if (isTopicModelCurationServiceType) {
        return `As your request is for a turnaround time less than three days, fulfillment is not guaranteed but our team will be in touch soon to confirm estimated time of completion.`;
    }
    const role = isInterpretingServiceType ? 'live interpreter' : 'live captioner';
    return `As this booking will take place in less than ${minutesToHumanRepresentation(guaranteedPeriodMinutes)} from now, fulfillment is not guaranteed but our team will be in touch soon to let you know whether a ${role} is available.`;
}

export function effectiveMinimumSessionDurationMinutes(
    billingMinMinutes: number,
    isOnsite: boolean
): number {
    const base = billingMinMinutes ?? 0;
    return isOnsite ? Math.max(base, ONSITE_CAPTIONING_MINIMUM_DURATION_MINUTES) : base;
}

export const truncateString = (str: string, length?: number) => {
    const newLen = length || 10
    return str.length > newLen ? `${str.substring(0, newLen)}...` : str
}

export const getBookingTypeTile = (type: string) => {
    switch (type) {
        case 'education':
            return ' Education class'
        case 'event':
            return 'Event'
            // istanbul ignore next -- unreachable: return above always exits the case
            break;
        case 'meeting':
        default:
            return 'Meeting'
    }
}

export const removeEmptyAndNull = (obj: any) => {
    let newObj: any = {};
    Object.keys(obj).forEach((key) => {
        if (obj[key] === Object(obj[key]) && !Array.isArray(obj[key])) {
            newObj[key] = removeEmptyAndNull(obj[key])
        } else if (obj[key] !== undefined && obj[key] !== null) {
            newObj[key] = obj[key]
        }
    });
    return newObj;
  };
export const debouncer = (func: Function, timeout = 300) =>{
    let timer: any;
    return (...args: any) => {
      clearTimeout(timer);
      timer = setTimeout(() => { func.apply(this, args); }, timeout);
    };
  }

// validations
export const constructErrorMessage = (
    form: UntypedFormGroup | UntypedFormControl,
    controlName?: string
): string => {
    let control: AbstractControl | null = null;

    // Determine control based on input type and presence of controlName
    if (form instanceof UntypedFormGroup && controlName) {
      control = form.get(controlName);
    } else if (form instanceof UntypedFormControl || (!controlName && 'errors' in form)) {
      control = form; // Treat as direct control
    }

    if (!control || (!control.dirty && !control.touched) || !control.errors) return '';

    const errors = control.errors;

    if (errors['required']) {
      return 'This field is required.';
    }

    if (errors['minlength']) {
      return `Minimum ${errors['minlength'].requiredLength} characters required.`;
    }

    if (errors['maxlength']) {
      return `Maximum ${errors['maxlength'].requiredLength} characters allowed.`;
    }

    if (errors['email'] || errors['invalidEmail']) {
      return 'Please enter a valid email address.';
    }

    if (errors['noSpecialChars']) {
      return 'No special characters allowed.';
    }

    if (errors['startsWithLetter']) {
      return 'Must start with a letter.';
    }

    if (errors['noNumbers']) {
      return 'Numbers are not allowed.';
    }

    if (errors['mustInclude']) {
      return `Must include the word "${errors['mustInclude']}".`;
    }

    if (errors['invalidNumber']) {
      return 'Please enter a valid number (or comma-separated numbers).';
    }

    return '';
};

export function strictEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return value && !emailRegex.test(value) ? { invalidEmail: true } : null;
    };
}

// RFC 5322 Official Standard Regex: https://www.rfc-editor.org/rfc/rfc5322
const rfc5322EmailRegex = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|(?:\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\]))$/;

export function rfcEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (!value) {
            return null; // skip validation if empty
        }
        return rfc5322EmailRegex.test(value) ? null : { invalidEmail: true };
    };
}

export function noSpecialCharsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        // Allow letters, numbers, space, and these symbols:
        // @ # $ ! - + = ' " : & ( ) * / [ ] < > ?
        const regex = /[^a-zA-Z0-9 @#$!\-+=:'"&()*\/\[\]<>?.]/;
        if (value && regex.test(value)) {
            return { noSpecialChars: true };
        }
        return null;
    };
}

export function speakerNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        // Allow Unicode letters, combining marks, digits, space, hyphen, apostrophe, period
        const regex = /[^\p{L}\p{M}\d \-'.]/u;
        if (value && regex.test(value)) {
            return { noSpecialChars: true };
        }
        return null;
    };
}

export function startsWithLetterValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      return value && !/^[A-Za-z]/.test(value)
        ? { startsWithLetter: true }
        : null;
    };
}

export function noNumbersValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      return value && /\d/.test(value) ? { noNumbers: true } : null;
    };
}

export function mustIncludeWord(word: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.toLowerCase();
      return value && !value.includes(word.toLowerCase())
        ? { mustInclude: word }
        : null;
    };
}

type TAccountDefaultLabelMode = 'be-name-only' | 'org-and-be';

function flattenBookableBillingEntities(
    bookableEntities: IBookableEntity[],
    mode: TAccountDefaultLabelMode,
): ISelectOption[] {
    const byId = new Map<number, ISelectOption>();
    for (const org of bookableEntities) {
        for (const be of org.billing_entities || []) {
            if (byId.has(be.billing_entity_id)) {
                continue;
            }
            const title =
                mode === 'org-and-be'
                    ? `${org.name} — ${be.name}`
                    : be.name;
            byId.set(be.billing_entity_id, {
                value: be.billing_entity_id,
                title,
            });
        }
    }
    return [...byId.values()].sort((a, b) =>
        a.title.localeCompare(b.title, undefined, {sensitivity: 'base'}),
    );
}

/**
 * Options for portal "Account default" (profile / user admin).
 * Super admins: all bookable billing entities, label = billing entity name only.
 * Users in more than one organisation: all bookable billing entities across those orgs, label = "Organisation — Account".
 * Single-organisation users: billing entities for the current organisation only.
 */
export function mapBookableEntitiesToAccountDefaultSelectOptions(
    bookableEntities: IBookableEntity[],
    currentOrgId: number,
    isSuperAdmin: boolean,
): ISelectOption[] {
    if (!bookableEntities?.length) {
        return [];
    }
    if (isSuperAdmin) {
        return flattenBookableBillingEntities(bookableEntities, 'be-name-only');
    }
    if (bookableEntities.length > 1) {
        return flattenBookableBillingEntities(bookableEntities, 'org-and-be');
    }
    const match = bookableEntities.filter((ent) => ent.org_id === currentOrgId);
    if (!match.length) {
        return [];
    }
    return match[0].billing_entities.map((be) => ({
        value: be.billing_entity_id,
        title: be.name,
    }));
}

/**
 * All LBP billing entities the user may book under (from getBookableEntities).
 * Labels include organisation when the user has more than one organisation.
 * Used for the create-booking Step 1 account picker when they have more than one account.
 */
export function mapBookableEntitiesToLbpAccountPickerOptions(bookableEntities: IBookableEntity[]): ISelectOption[] {
    if (!bookableEntities?.length) {
        return [];
    }
    const multiOrg = bookableEntities.length > 1;
    const mode: TAccountDefaultLabelMode = multiOrg ? 'org-and-be' : 'be-name-only';
    return flattenBookableBillingEntities(bookableEntities, mode);
}

export function mapBookableEntitiesToOrgSelectOptions(bookableEntities: IBookableEntity[]): ISelectOption[] {
    if (!bookableEntities?.length) {
        return [];
    }
    return [...bookableEntities]
        .map((o) => ({ value: o.org_id, title: o.name }))
        .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}

/**
 * Billing entities for a single organisation (create-booking Step 1 — always org-scoped, including super admins).
 */
export function mapBookableEntitiesToBillingEntitiesInOrg(
    bookableEntities: IBookableEntity[],
    orgId: number,
): ISelectOption[] {
    if (!bookableEntities?.length || orgId == null) {
        return [];
    }
    const match = bookableEntities.filter((ent) => ent.org_id === orgId);
    if (!match.length) {
        return [];
    }
    return match[0].billing_entities.map((be) => ({
        value: be.billing_entity_id,
        title: be.name,
    }));
}

/**
 * Default billing entity within an org: portal account_default when listed, else preferredBe if listed, else first.
 */
export function resolveDefaultBillingEntityIdInOrg(
    bookableEntities: IBookableEntity[],
    orgId: number,
    accountDefault: number | undefined | null,
    preferredBillingEntityId: number,
): number {
    const opts = mapBookableEntitiesToBillingEntitiesInOrg(bookableEntities, orgId);
    if (!opts.length) {
        return preferredBillingEntityId;
    }
    const ids = new Set(opts.map((o) => o.value as number));
    if (accountDefault != null && `${accountDefault}` !== '' && !Number.isNaN(+accountDefault) && ids.has(+accountDefault)) {
        return +accountDefault;
    }
    if (preferredBillingEntityId && ids.has(preferredBillingEntityId)) {
        return preferredBillingEntityId;
    }
    return opts[0].value as number;
}

async function fetchWithRetry(url: string, opts: RequestInit, retries = 3): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await fetch(url, opts);
      if (res.ok) return res;
      lastError = new Error(`HTTP ${res.status}`);
    } catch (err) {
      lastError = err;
    }
    if (attempt < retries - 1) await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
  }
  throw lastError || new Error('Request failed after retries');
}

export async function chunkedUpload(
  file: File,
  params: { org_id: number; type: string; content: string; metadata?: Record<string, unknown> },
  onProgress?: (percent: number) => void,
  onAccepted?: () => void,
): Promise<any> {
  if (file.size === 0) throw new Error('Cannot upload empty file');
  const CHUNK_SIZE = 0.5 * 1024 * 1024; // 512 KB
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const authHeader = { Authorization: 'Bearer ' + getAppToken() };

  // 1. Init — registers the session and returns uploadId
  const initRes = await fetchWithRetry(getBackendBaseEndpoint('/v1/upload/init'), {
    method: 'POST',
    headers: { ...authHeader, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      org_id: params.org_id,
      type: params.type,
      filename: file.name,
      totalChunks,
      content: params.content,
      metadata: params.metadata,
    }),
  });
  if (!initRes.ok) throw new Error(`Upload init failed (HTTP ${initRes.status})`);
  const { uploadId } = await initRes.json();

  // 2. Upload chunks sequentially with per-chunk retry
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const fd = new FormData();
    fd.append('uploadId', uploadId);
    fd.append('chunkIndex', String(i));
    fd.append('chunk', file.slice(start, Math.min(start + CHUNK_SIZE, file.size)), file.name);
    await fetchWithRetry(getBackendBaseEndpoint('/v1/upload/chunk'), {
      method: 'POST',
      headers: authHeader,
      body: fd,
    });
    onProgress?.((i + 1) / totalChunks * 90); // 0–90% for chunk phase
  }

  // 3. Complete — kicks off async merge, returns 202 immediately
  const completeRes = await fetchWithRetry(getBackendBaseEndpoint('/v1/upload/complete'), {
    method: 'POST',
    headers: { ...authHeader, 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadId }),
  });
  if (!completeRes.ok) throw new Error(`Upload complete failed (HTTP ${completeRes.status})`);
  const completeData = await completeRes.json();

  // Notify caller that the upload was accepted — safe to show success UI now
  onAccepted?.();

  // 4. Poll until the background merge+DB work finishes
  if (completeData.status === 'processing') {
    const POLL_INTERVAL_MS = 2000;
    const POLL_TIMEOUT_MS = 5 * 60 * 1000;
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise<void>((r) => setTimeout(r, POLL_INTERVAL_MS));
      const statusRes = await fetch(getBackendBaseEndpoint(`/v1/upload/status/${uploadId}`), {
        headers: authHeader,
      });
      if (!statusRes.ok) throw new Error(`Status check failed (HTTP ${statusRes.status})`);
      const statusData = await statusRes.json();
      if (statusData.state === 'done') {
        onProgress?.(100);
        return statusData.result;
      }
      if (statusData.state === 'error') {
        throw new Error(statusData.error || 'Upload processing failed');
      }
    }
    throw new Error('Upload timed out waiting for server to finish processing');
  }

  onProgress?.(100);
  return completeData;
}

// same rule as the backend gate: an explicit switch wins, older accounts map from the route picker
export const isApiEnabled = (settings: any): boolean => {
    const api = settings?.api;
    if (typeof api?.enabled === 'boolean') return api.enabled;
    return api?.allow_all === true || (Array.isArray(api?.enabled_routes) && api.enabled_routes.length > 0);
};
